import { forwardRef, useState } from 'react';
import type { TextareaProps } from './Textarea.types';
import { labelVariants } from '../Input/Input';
import { cn } from '../../lib/utils';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      description,
      helperText,
      error = false,
      errorMessage,
      actionText,
      fullWidth = false,
      textareaSize = 'base',
      resize = 'vertical',
      className = '',
      disabled = false,
      id,
      rows = 4,
      ...props
    },
    ref
  ) => {
    const [generatedId] = useState(() => Math.random().toString(36).substring(2, 9));
    const textareaId = id || `textarea-${generatedId}`;

    const sizeClasses = {
      xs: "px-2 py-1 text-xs",
      sm: "px-2 py-1 text-sm",
      base: "px-3 py-1.5 text-base",
      l: "px-3 py-2 text-lg",
      xl: "px-4 py-2 text-xl",
    };

    return (
      <div className={cn("grid w-full items-center gap-2", className)}>
        {/* Label y Descripción - Descripción abajo del label */}
        {(label || actionText || description) && (
          <div className="flex flex-col gap-2 w-full">
            {(label || actionText) && (
              <div className="flex items-center justify-between w-full">
                {label && (
                  <label
                    htmlFor={textareaId}
                    className={cn("text-content-primary dark:text-dark-content-primary", labelVariants({ size: textareaSize }))}
                  >
                    {label}
                    {props.required && <span className="text-destructive ml-1">*</span>}
                  </label>
                )}
                {actionText && (
                  <a href="#" className="text-xs font-medium text-primary dark:text-primary-300 cursor-pointer hover:underline">
                    {actionText}
                  </a>
                )}
              </div>
            )}
            {description && (
              <p className={`${textareaSize === 'xs' ? 'text-xs' : 'text-sm'} ${error ? 'text-destructive' : 'text-muted-foreground'}`}>
                {description}
              </p>
            )}
          </div>
        )}

        {/* Textarea Field */}
        <textarea
          ref={ref}
          id={textareaId}
          disabled={disabled}
          rows={rows}
          required={props.required}
          className={`
            flex w-full rounded-md border border-input bg-transparent transition-colors
            placeholder:text-muted-foreground
            focus-visible:outline-none focus:ring-1 focus:ring-primary-400 focus:border-primary-400 focus-within:ring-1 focus-within:ring-primary-400 focus-within:border-primary-400
            disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-zinc-100 dark:disabled:bg-zinc-800/80 disabled:text-zinc-500 dark:disabled:text-zinc-400

            ${sizeClasses[textareaSize]}

            ${resize === 'none' ? 'resize-none' : resize === 'vertical' ? 'resize-y' : resize === 'horizontal' ? 'resize-x' : 'resize'}

            ${error ? 'border-destructive ring-1 ring-destructive focus:ring-destructive focus:border-destructive focus-within:ring-destructive focus-within:border-destructive text-destructive placeholder:text-destructive/60' : ''}

            ${props.readOnly ? 'bg-zinc-100 dark:bg-zinc-800/80 text-zinc-500 dark:text-zinc-400 focus-visible:ring-0 focus-visible:border-input focus-visible:ring-offset-0 cursor-default' : ''}
          `.replace(/\s+/g, ' ').trim()}
          {...props}
        />

        {/* Helper Text o Mensaje de Error */}
        {(helperText || errorMessage) && (
          <p className={`${textareaSize === 'xs' ? 'text-xs' : 'text-sm'} ${error ? 'text-destructive' : 'text-muted-foreground'}`}>
            {error && errorMessage ? errorMessage : helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
